/**
 * CalcParamsModal — modal dialog for editing calculation parameters per analysis section.
 *
 * Each analysis module (delinquencies, dilution, buyerParent, concentrationLimits) can
 * have configurable parameters. This modal renders a form based on a field definition array,
 * initializes values from Redux, and dispatches SET_CALC_PARAMS on save.
 *
 * Also exports ParamsButton — a small gear icon button to open the modal.
 */
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FIELD_TYPES } from "Config/calcParamsConfig";

const CalcParamsModal = ({ section, title, fields, onClose }) => {
  const dispatch = useDispatch();
  const calcParams = useSelector((state) => state.project.calcParams);

  const [values, setValues] = useState({});

  useEffect(() => {
    const currentParams = (calcParams || {})[section] || {};
    const init = {};
    fields.forEach((f) => {
      init[f.key] = currentParams[f.key] !== undefined ? currentParams[f.key] : f.default;
    });
    setValues(init);
  }, [section, fields, calcParams]);

  // Parses input values using field-type-specific parsers (e.g., int, float, string)
  const handleChange = (key, fieldType, rawValue) => {
    const ft = FIELD_TYPES[fieldType] || FIELD_TYPES.string;
    setValues((prev) => ({ ...prev, [key]: ft.parse(rawValue) }));
  };

  const handleSave = () => {
    dispatch({ type: "SET_CALC_PARAMS", section, params: values });
    onClose();
  };

  return (
    <div className="modal is-active">
      <div className="modal-background" onClick={onClose}></div>
      <div className="modal-card" style={{ maxWidth: "480px" }}>
        <header className="modal-card-head">
          <p className="modal-card-title" style={{ fontSize: "0.9rem" }}>{title}</p>
          <button className="delete" aria-label="close" onClick={onClose}></button>
        </header>
        <section className="modal-card-body">
          <table className="table is-fullwidth">
            <tbody>
              {fields.map((f) => {
                const ft = FIELD_TYPES[f.type] || FIELD_TYPES.string;
                return (
                  <tr key={f.key}>
                    <td style={{ verticalAlign: "middle" }}>
                      <div style={{ fontWeight: 600, fontSize: "0.8rem" }}>
                        {f.label}
                        {f.unit && (
                          <span style={{ color: "var(--ev-text-muted)", fontWeight: 400, marginLeft: "0.3rem" }}>
                            ({f.unit})
                          </span>
                        )}
                      </div>
                      {f.description && (
                        <div style={{ fontSize: "0.7rem", color: "var(--ev-text-muted)", marginTop: "0.15rem", lineHeight: 1.3 }}>
                          {f.description}
                        </div>
                      )}
                    </td>
                    <td style={{ width: "140px", verticalAlign: "middle" }}>
                      <input
                        className="input is-small"
                        type={ft.type}
                        step={ft.step}
                        value={values[f.key] !== undefined ? values[f.key] : ""}
                        onChange={(e) => handleChange(f.key, f.type, e.target.value)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
        <footer className="modal-card-foot" style={{ justifyContent: "flex-end" }}>
          <button className="button is-small" onClick={onClose}>Cancel</button>
          <button className="button is-small is-success" onClick={handleSave}>Save</button>
        </footer>
      </div>
    </div>
  );
};

export default CalcParamsModal;

export const ParamsButton = ({ onClick }) => (
  <button
    className="button is-small"
    onClick={onClick}
    style={{ borderRadius: "6px" }}
    title="Calculation Parameters"
  >
    <span className="icon" style={{ fontSize: "0.8rem" }}>
      <i className="fa-duotone fa-gear" />
    </span>
  </button>
);
